import React, { useEffect, useRef } from 'react';
import Header from './Header';
import Footer from './Footer';
import MobileStickyCallBar from './MobileStickyCallBar';
import { useSettings } from '@/lib/settings';

/**
 * Inject custom scripts/markup into either <head> or before </body>.
 * Re-creates <script> tags so the browser actually executes them
 * (necessary because innerHTML doesn't run script content).
 */
function ScriptsInjector({ html, target }) {
    // target: 'head' | 'body'
    useEffect(() => {
        if (!html) return;
        const parent = target === 'head' ? document.head : document.body;
        if (!parent) return;

        // Parse the HTML
        const template = document.createElement('template');
        template.innerHTML = html;

        // Track our injected nodes so we can clean them up later
        const injected = [];

        const nodes = Array.from(template.content.childNodes);
        nodes.forEach((node) => {
            // Skip text nodes that are only whitespace
            if (node.nodeType === 3 && !node.textContent.trim()) return;

            if (node.nodeName === 'SCRIPT') {
                // Re-create script element so browser executes it
                const s = document.createElement('script');
                for (const attr of node.attributes) {
                    s.setAttribute(attr.name, attr.value);
                }
                if (node.textContent) s.text = node.textContent;
                s.setAttribute('data-injected-by', `sk-${target}`);
                parent.appendChild(s);
                injected.push(s);
            } else if (node.nodeType === 1) {
                // Element node (meta, link, noscript, etc.)
                const clone = node.cloneNode(true);
                clone.setAttribute && clone.setAttribute('data-injected-by', `sk-${target}`);
                parent.appendChild(clone);
                injected.push(clone);
            } else if (node.nodeType === 3) {
                // Text node - skip (we don't inject raw text into head/body)
            }
        });

        return () => {
            // Cleanup on unmount or html change
            injected.forEach((el) => {
                try { el.parentNode && el.parentNode.removeChild(el); } catch { /* noop */ }
            });
        };
    }, [html, target]);

    return null;
}

export default function PublicLayout({ children }) {
    const { settings } = useSettings();
    const headScripts = settings?.custom_head_scripts || '';
    const footerScripts = settings?.custom_footer_scripts || '';

    return (
        <div className="min-h-screen flex flex-col bg-[color:var(--sk-bg)]">
            <ScriptsInjector html={headScripts} target="head" />
            <Header />
            <main className="flex-1 pb-24 md:pb-0" data-testid="main-content">
                {children}
            </main>
            <Footer />
            <MobileStickyCallBar />
            <ScriptsInjector html={footerScripts} target="body" />
        </div>
    );
}
